// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * The sites a course's published card is asked of, in the order the button asks them. Each one
 * declines a club outside its own country before requesting anything, so for any one course at
 * most one of them does real work — the same pair, in the same order, the analysis asks.
 */
export const SCORECARD_SOURCES = ['golfisverige', 'golfingholiday'] as const;

export type ScorecardSource = (typeof SCORECARD_SOURCES)[number];

/** What one source said when asked for this course's card. */
export type ScorecardFetchAnswer =
  | {
      status: 'answered';
      /** The page it read, or would have read; null when it never got as far as one. */
      url: string | null;
      holes: number;
      tees: number;
      /** The service's own sentence for what happened, or its one-word outcome. */
      reason: string | null;
    }
  /** No answer worth reading: the status when there was one, null when nothing came back. */
  | { status: 'failed'; httpStatus: number | null };

/**
 * Asks one source for this course's card, now — forced and attended server-side, like the admin
 * list's "Import GIS" button. Never throws: every way it can go wrong is an answer to show.
 */
export async function fetchScorecard(
  apiBaseUrl: string,
  token: string,
  courseId: string,
  source: ScorecardSource
): Promise<ScorecardFetchAnswer> {
  try {
    const response = await fetch(
      `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/scorecard/import?source=${source}`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) return { status: 'failed', httpStatus: response.status };
    const body = (await response.json()) as {
      url?: string | null;
      holesWritten?: number;
      teesWritten?: number;
      reason?: string | null;
      outcome?: string | null;
    };
    return {
      status: 'answered',
      url: body.url ?? null,
      holes: body.holesWritten ?? 0,
      tees: body.teesWritten ?? 0,
      reason: body.reason ?? body.outcome ?? null,
    };
  } catch {
    return { status: 'failed', httpStatus: null };
  }
}
